import { getPrivacyPolicyContent } from "api/rental-agreement/rental-agreement";
import { useEffect, useState } from "react";
import { formatPhoneLabel } from "utils/PhoneFormat";
import { useData } from "data/data-provider";

/**
 * 1NOW-10372: standalone, publicly linkable policy pages.
 *
 * Tenants have to hand these URLs to third parties — an SMS carrier will not
 * approve a campaign until it can open a privacy policy page carrying the SMS
 * disclosures — so both designs route /privacy-policy and /terms-and-conditions
 * here and both footers link to them.
 *
 * Content is the same company-authored copy the checkout modals show; it comes
 * from the public (unauthenticated) /api/company/content/ endpoint grouped by
 * policy type.
 *
 * NOTE (branch difference): dev and QA wrap the rendered copy in
 * `sanitizeHtml()` from 1NOW-1643. That util — and its `dompurify` dependency —
 * are not on this branch, and every other policy surface here already renders
 * this same tenant HTML raw, so the port keeps the existing behaviour rather
 * than dragging an unrelated security ticket along with it. When 1NOW-1643 is
 * promoted, re-wrap the `__html` below to match dev/QA.
 */
export type PolicyKind = "privacy_policy" | "terms_condition";

interface PolicyPageProps {
  kind: PolicyKind;
  title: string;
}

const PolicyPage = ({ kind, title }: PolicyPageProps) => {
  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { ourCompany } = useData();

  useEffect(() => {
    const getContent = async () => {
      setLoading(true);
      try {
        const response = await getPrivacyPolicyContent();
        // The endpoint only returns a key for policy types the company has
        // actually authored, so default to an empty list.
        setContent(response?.data?.[kind] ?? []);
      } catch (error) {
        console.error("Error fetching content", error);
      } finally {
        setLoading(false);
      }
    };

    getContent();
  }, [kind]);

  // Same tokens the checkout policy modals resolve.
  const resolveTokens = (html?: string) =>
    (html ?? "")
      .replace(
        /\{\{phone\}\}/g,
        formatPhoneLabel(ourCompany?.company?.phone_no),
      )
      .replace(/\{\{email\}\}/g, ourCompany?.company?.email ?? "")
      .replace(/\{\{address\}\}/g, ourCompany?.company?.address ?? "")
      .replace(
        /\{\{deposit amount\}\}/g,
        "$" + ourCompany?.default_deposit_amount,
      );

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mt-6 min-h-[60vh] rounded-lg bg-bg-color p-4">
        <div className="mb-8">
          <h1 className="text-center text-3xl font-bold text-text-color sm:text-4xl">
            {title}
          </h1>
        </div>

        {loading ? (
          <div className="col-span-full flex min-h-[40vh] items-center justify-center">
            <div className="spinner-border inline-block h-6 w-6 animate-spin rounded-full border-4 border-t-transparent border-secondary-100"></div>
          </div>
        ) : (
          <div className="col-span-12 container">
            <div className="flex flex-col gap-y-4">
              {content.length > 0 ? (
                content.map((item: any, i: number) => (
                  <div className="rounded-lg border border-muted" key={i}>
                    <div className="header rounded-lg bg-text-color/5 px-5 py-3">
                      <h2 className="text-lg text-text-color">
                        {i + 1}. {item?.title}
                      </h2>
                    </div>
                    <div className="header w-full border-t border-muted p-5">
                      <div className="grid w-full grid-cols-1 gap-3 rounded-lg">
                        <div
                          className="prose prose-sm ql-content break-words text-text-color"
                          style={{ maxWidth: "100%" }} // Manually remove the max width
                          dangerouslySetInnerHTML={{
                            __html: resolveTokens(item?.content),
                          }}
                        />
                      </div>
                    </div>
                    {item?.content_picture && item?.content_picture !== "null" && (
                      <div className="header border-t border-muted p-5">
                        <div className="grid grid-cols-1 gap-3 rounded-lg">
                          <img
                            alt={title}
                            src={`https://fleet-management-images-upload-be.s3.amazonaws.com/${item?.content_picture}`}
                            height={660}
                            width={600}
                            className="w-full rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Tenants who have not written this policy yet still land on a
                // real page rather than a 404 — the footer links are always
                // rendered, and a third party opening the URL needs to see
                // something coherent.
                <p className="text-center text-text-color">
                  No content available.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Route-ready wrappers. The router `pages` array takes plain
 * `ComponentType<Object>` entries, so the policy kind is bound here rather than
 * passed as a route prop — and both designs stay on the same two URLs.
 */
export const PrivacyPolicyPage = () => (
  <PolicyPage kind="privacy_policy" title="Privacy Policy" />
);

export const TermsAndConditionsPage = () => (
  <PolicyPage kind="terms_condition" title="Terms & Conditions" />
);

export default PolicyPage;
